import { inject } from "@angular/core";
import { SignalSubscriptionImpl } from "../data-subscription";
import { UserStoreService } from "../../../services/store/user/user.store.service";
import { CabinetTableView } from "../../../models/types/cabinet.types";
import { InstitutionView } from "../../../models/view/institution/institution.view";
import { AccountView } from "../../../models/view/account/account.view";
import { getCabinetFileIconMeta } from "../../../models/view/vault-file/cabinet-file-icon.util";

export type TableSource = CabinetTableView;

export class CabinetTableSubscription extends SignalSubscriptionImpl<TableSource[]> {
  private userStore: UserStoreService = inject(UserStoreService);

  get source() {
    // These collections start as null before their first load; ?? [] here covers every read
    // below in one place rather than each call site guarding separately.
    //
    // This also collapses the null-loading sentinel that deepLinkParamReady$ (cabinet.component.ts)
    // gates on into an empty array, so a fileId deep link would be swept from the URL as "stale"
    // if this component ever mounted mid-load. That's safe only because it can't today:
    // InitialRedirectGuard sits on the shared "" route (oss-routing.module.ts) and redirects away
    // from a cold entry to /cabinet unless the URL already carries views/redirect/jobId/settings/bundles,
    // so vaultFileViews is always populated by the time CabinetComponent constructs. If that guard
    // is ever loosened to let /cabinet survive a cold load, restore the sentinel here
    // (`if (!vaultFileViews || !accountView) return null;`) before relying on ?? [] again.
    const accountView = this.userStore.enrichAccounts.collection() ?? [];
    const vaultFileViews = this.userStore.vaultFiles.vaultFileViews() ?? [];
    const institutionView = this.userStore.institutions.institutionViews() ?? [];
    const cabinetData: CabinetTableView[] = [];

    vaultFileViews.forEach((file) => {
      const accounts = accountView.filter((acc) => file.accountIds.includes(acc.id));
      const banks: InstitutionView[] = [];
      const statementToDate = file.statementToDate ?? "";
      const statementFromDate = file.statementFromDate ?? "";

      accounts.forEach((acc) => {
        const institutions = this.institution(institutionView, acc);
        if (institutions) {
          banks.push(institutions);
        }
      });

      cabinetData.push({
        fileAndAccount: {
          file,
          banks,
          accounts: accounts,
        },
        statementPeriod: {
          fromDate: statementFromDate,
          toDate: statementToDate,
        },
        rightChevron: true,
        iconMeta: getCabinetFileIconMeta(file.name),
        linkedAccountNames: accounts.map((account) => account.name).join(", "),
      });
    });

    // name is nullable at runtime (strictNullChecks off)
    return cabinetData.sort((a, b) =>
      (a.fileAndAccount.file.name ?? "").localeCompare(b.fileAndAccount.file.name ?? ""),
    );
  }

  institution(institutionView: InstitutionView[], accountView: AccountView) {
    return institutionView.find(
      (institution) => institution.id === accountView.institutionLink.institutionId,
    );
  }
}
